import { useMemo } from 'react';
import { ErrorMessage } from '@asset-metrix/portal-ui';
import clsx from 'clsx';
import { TFunction } from 'i18next';

import { FilterConfigDto, FilterData } from '../../store/states/filterTypes';

import { ComparisonPageFilter } from './ComparisonPageFilter';
import { Filter } from './Filter';
import { FilterActions } from './FilterActions';
import { type FilterPersistenceMode } from './FilterPersistenceIcon';
import { getFilterFormatters } from './formatter';
import { isDualInputRangeFilterDisplay } from './util';

import styles from './filters.module.scss';

export type FiltersComponentProps = {
  config: { filters?: FilterConfigDto[] };
  isFetching: boolean;
  entityId: string;
  resetFilterSelection: (pageId: string) => void;
  error: Error | null;
  translate: TFunction<'translation', undefined>;
  showFilterActions?: boolean;
  disableDynamicWidth?: boolean;
  hideSelectAll?: boolean;
  validatedData?: Record<string, FilterData>;
  filterTestId?: string;
  persistenceMode?: FilterPersistenceMode;
  onPersistenceClick?: () => void;
};

export function FiltersComponent({
  config: { filters = [] },
  isFetching,
  entityId,
  resetFilterSelection,
  error,
  translate,
  showFilterActions = true,
  disableDynamicWidth = false,
  hideSelectAll = false,
  validatedData,
  filterTestId,
  persistenceMode,
  onPersistenceClick,
}: FiltersComponentProps) {
  const showResetIcon = useMemo(
    () => filters.some((configDto) => configDto.requiresSelection !== true),
    [filters]
  );

  const comparisonFilters = filters.filter((f) => f.display === 'comparison');
  if (comparisonFilters.length > 1) {
    throw new Error(
      translate(
        'app.pageFilter.comparisonFilter.multipleComparisonFiltersError'
      )
    );
  }

  const content = (
    <>
      {filters.map((configDto, index) => {
        const filterData = validatedData?.[configDto.key];

        const pageFilterClasses = clsx(
          disableDynamicWidth && styles['pagefilter__filter--full-width'],
          !disableDynamicWidth &&
            isDualInputRangeFilterDisplay(configDto.display) &&
            styles['pagefilter__filter--no-width']
        );

        const comparisonDropdownClasses = clsx(
          styles['pagefilter__filter'],
          pageFilterClasses
        );

        return (
          <div
            className={clsx(
              styles['pagefilter__wrapper'],
              disableDynamicWidth && styles['pagefilter__wrapper--full-width']
            )}
            data-testid={filterTestId}
            key={configDto.key}
          >
            <div
              className={clsx(
                configDto.display !== 'comparison' &&
                  styles['pagefilter__filter'],
                pageFilterClasses
              )}
            >
              {configDto.display === 'comparison' ? (
                <ComparisonPageFilter
                  classNames={{
                    baseDropdown: comparisonDropdownClasses,
                    comparisonDropdown: comparisonDropdownClasses,
                    root: clsx(
                      index > 0 &&
                        styles['pagefilter__wrapper--separator-left'],
                      index + 1 < filters.length &&
                        styles['pagefilter__wrapper--separator-right']
                    ),
                  }}
                  entityId={entityId}
                  filterData={
                    filterData?.display === 'comparison'
                      ? filterData
                      : undefined
                  }
                  filterKey={configDto.key}
                  formatter={getFilterFormatters(configDto)}
                  isFetching={isFetching}
                />
              ) : (
                <Filter
                  isFetching={isFetching}
                  filterConfig={configDto}
                  entityId={entityId}
                  disableDynamicWidth={disableDynamicWidth}
                  hideSelectAll={hideSelectAll}
                  filterData={filterData}
                  key={index}
                />
              )}
            </div>
            {showFilterActions && index + 1 === filters.length && (
              <FilterActions
                showResetIcon={showResetIcon}
                resetTitle={translate('app.pageFilter.reset')}
                onResetClick={() => resetFilterSelection(entityId)}
                persistenceMode={persistenceMode}
                onPersistenceClick={onPersistenceClick}
              />
            )}
          </div>
        );
      })}
      {error != null && (
        <div>
          <ErrorMessage>{error.message}</ErrorMessage>
        </div>
      )}
    </>
  );

  return content;
}
